import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { InvoiceDetailResponse, InvoicePart } from '../../../types/invoice';
import type { LineItemField } from './invoiceDetailFormTypes';
import { lineBackendMapAfterDelete, lineBackendMapAfterInsert } from '../invoiceDetailFormMappings';
import { applyAutoDerivedUnitPrice } from './invoiceDetailFormPartMath';
import { isFreightLineItem, isMiscFeePart } from './invoiceDetailFormLineClassification';
import { createPartFieldGetters } from './invoiceDetailFormPartTabGetters';

type PartListField = 'lineItems' | 'freightItems' | 'fuelItems' | 'restockingFeeItems';

export interface UseInvoiceDetailFormPartTabsParams {
    invoice: InvoiceDetailResponse | undefined;
    editedFields: Partial<InvoiceDetailResponse>;
    setEditedFields: Dispatch<SetStateAction<Partial<InvoiceDetailResponse>>>;
    newLineItem: () => InvoicePart;
    normalizeReturnLineFieldValue: (field: LineItemField, value: unknown) => unknown;
    shiftReconciliationIndicesAfterInsert: (insertedGlobalIndex: number) => void;
    shiftReconciliationIndicesAfterDelete: (deletedIndex: number) => void;
    reviseLineBackendMap: (
        nextItems: InvoicePart[],
        revise: (prev: readonly (number | undefined)[]) => (number | undefined)[],
    ) => void;
}

function removeRowWithOptionalMoveToIgnored(
    setEditedFields: Dispatch<SetStateAction<Partial<InvoiceDetailResponse>>>,
    tabField: PartListField,
    index: number,
    current: InvoicePart[],
    ignoredAtCallTime: InvoicePart[],
) {
    const removed = current[index];
    const updated = current.filter((_, i) => i !== index);
    const isExtracted = removed?.confidence && removed.confidence > 0;
    if (isExtracted) {
        setEditedFields((prev) => ({
            ...prev,
            [tabField]: updated,
            ignoredParts: [...ignoredAtCallTime, removed],
        }));
    } else {
        setEditedFields((prev) => ({ ...prev, [tabField]: updated }));
    }
}

export function useInvoiceDetailFormPartTabs({
    invoice,
    editedFields,
    setEditedFields,
    newLineItem,
    normalizeReturnLineFieldValue,
    shiftReconciliationIndicesAfterInsert,
    shiftReconciliationIndicesAfterDelete,
    reviseLineBackendMap,
}: UseInvoiceDetailFormPartTabsParams) {
    const updateLineItem = (index: number, field: LineItemField, value: unknown) => {
        setEditedFields((prev) => {
            const updatedLineItems = [...(prev.lineItems || invoice?.lineItems || [])];
            const normalizedValue = normalizeReturnLineFieldValue(field, value);
            updatedLineItems[index] = applyAutoDerivedUnitPrice(
                updatedLineItems[index] as InvoicePart,
                field,
                normalizedValue,
            );
            reviseLineBackendMap(updatedLineItems, (map) => map.slice());
            return { ...prev, lineItems: updatedLineItems };
        });
    };

    const restoreLineItemRowsSnapshot = useCallback(
        (snapshots: ReadonlyArray<{ row: number; item: InvoicePart }>) => {
            if (snapshots.length === 0) return;
            setEditedFields((prev) => {
                const updatedLineItems = [...(prev.lineItems || invoice?.lineItems || [])];
                for (const { row, item } of snapshots) {
                    if (row >= 0 && row < updatedLineItems.length) {
                        updatedLineItems[row] = { ...item };
                    }
                }
                reviseLineBackendMap(updatedLineItems, (map) => map.slice());
                return { ...prev, lineItems: updatedLineItems };
            });
        },
        [invoice?.lineItems, reviseLineBackendMap, setEditedFields],
    );

    const addLineItem = () => {
        const currentLineItems = editedFields.lineItems || invoice?.lineItems || [];
        const next = [...currentLineItems, newLineItem()];
        reviseLineBackendMap(next, (map) => lineBackendMapAfterInsert(map, map.length));
        setEditedFields((prev) => ({ ...prev, lineItems: next }));
    };

    const addLineItemAbove = (index: number) => {
        const currentLineItems = editedFields.lineItems || invoice?.lineItems || [];
        const updated = [...currentLineItems];
        updated.splice(index, 0, newLineItem());
        shiftReconciliationIndicesAfterInsert(index);
        reviseLineBackendMap(updated, (map) => lineBackendMapAfterInsert(map, index));
        setEditedFields((prev) => ({ ...prev, lineItems: updated }));
    };

    const addLineItemAt = (index: number) => {
        const currentLineItems = editedFields.lineItems || invoice?.lineItems || [];
        const updated = [...currentLineItems];
        updated.splice(index + 1, 0, newLineItem());
        shiftReconciliationIndicesAfterInsert(index + 1);
        reviseLineBackendMap(updated, (map) => lineBackendMapAfterInsert(map, index + 1));
        setEditedFields((prev) => ({ ...prev, lineItems: updated }));
    };

    const duplicateLineItemAt = (index: number) => {
        const currentLineItems = editedFields.lineItems || invoice?.lineItems || [];
        const source = currentLineItems[index];
        if (!source) return;
        const duplicated: InvoiceDetailResponse['lineItems'][0] = { ...source, confidence: 0 };
        const updated = [...currentLineItems];
        updated.splice(index + 1, 0, duplicated);
        shiftReconciliationIndicesAfterInsert(index + 1);
        reviseLineBackendMap(updated, (map) => lineBackendMapAfterInsert(map, index + 1));
        setEditedFields((prev) => ({ ...prev, lineItems: updated }));
    };

    const removeLineItem = (index: number) => {
        const currentLineItems = editedFields.lineItems || invoice?.lineItems || [];
        if (index < 0 || index >= currentLineItems.length) return;
        const next = currentLineItems.filter((_, i) => i !== index);
        shiftReconciliationIndicesAfterDelete(index);
        reviseLineBackendMap(next, (map) => lineBackendMapAfterDelete(map, index));
        setEditedFields((prev) => ({ ...prev, lineItems: next }));
    };

    const updatePartTab = (tabField: PartListField, index: number, field: LineItemField, value: unknown) => {
        const updated = [...(editedFields[tabField] ?? invoice?.[tabField] ?? [])];
        const normalizedValue = normalizeReturnLineFieldValue(field, value);
        updated[index] = applyAutoDerivedUnitPrice(updated[index] as InvoicePart, field, normalizedValue);
        setEditedFields((prev) => ({ ...prev, [tabField]: updated }));
    };

    const newFreightItem = () => ({ ...newLineItem(), partNumber: 'freight' });
    const newFuelItem = () => ({ ...newLineItem(), title: 'Fuel', partNumber: 'fuel' });
    // Restocking fees are a positive charge even on credited invoices, so override the
    // return-invoice qty -1 default inherited from newLineItem().
    const newRestockingItem = () => ({ ...newLineItem(), title: 'Restocking Fee', partNumber: 'restocking', qty: 1 });

    const appendRow = (tabField: PartListField, row: InvoicePart) => {
        const current = editedFields[tabField] ?? invoice?.[tabField] ?? [];
        setEditedFields((prev) => ({ ...prev, [tabField]: [...current, row] }));
    };

    const insertRowBelow = (tabField: PartListField, index: number, row: InvoicePart) => {
        const current = editedFields[tabField] ?? invoice?.[tabField] ?? [];
        const updated = [...current];
        updated.splice(index + 1, 0, row);
        setEditedFields((prev) => ({ ...prev, [tabField]: updated }));
    };

    const insertRowAbove = (tabField: PartListField, index: number, row: InvoicePart) => {
        const current = editedFields[tabField] ?? invoice?.[tabField] ?? [];
        const updated = [...current];
        updated.splice(index, 0, row);
        setEditedFields((prev) => ({ ...prev, [tabField]: updated }));
    };

    const duplicateRowBelow = (tabField: PartListField, index: number) => {
        const current = editedFields[tabField] ?? invoice?.[tabField] ?? [];
        const source = current[index];
        if (!source) return;
        const duplicated: InvoicePart = { ...source, confidence: 0 };
        const updated = [...current];
        updated.splice(index + 1, 0, duplicated);
        setEditedFields((prev) => ({ ...prev, [tabField]: updated }));
    };

    const { getValue: getLineItemValue, getStringValue: getLineItemStringValue, getPriceValue: getLineItemPriceValue } =
        createPartFieldGetters((index, field) => {
            if (editedFields.lineItems?.[index]?.[field] !== undefined) return editedFields.lineItems[index][field];
            return invoice?.lineItems?.[index]?.[field] ?? '';
        });

    const { getValue: getFreightItemValue, getStringValue: getFreightItemStringValue, getPriceValue: getFreightItemPriceValue } =
        createPartFieldGetters((index, field) =>
            editedFields.freightItems?.[index]?.[field] ?? invoice?.freightItems?.[index]?.[field] ?? '',
        );

    const { getValue: getFuelItemValue, getStringValue: getFuelItemStringValue, getPriceValue: getFuelItemPriceValue } =
        createPartFieldGetters((index, field) =>
            editedFields.fuelItems?.[index]?.[field] ?? invoice?.fuelItems?.[index]?.[field] ?? '',
        );

    const {
        getValue: getRestockingFeeItemValue,
        getStringValue: getRestockingFeeItemStringValue,
        getPriceValue: getRestockingFeeItemPriceValue,
    } = createPartFieldGetters((index, field) =>
        editedFields.restockingFeeItems?.[index]?.[field] ?? invoice?.restockingFeeItems?.[index]?.[field] ?? '',
    );

    const getIgnoredList = (): InvoiceDetailResponse['lineItems'] =>
        editedFields.ignoredParts ??
        invoice?.ignoredParts ??
        (invoice != null && 'canceledParts' in invoice
            ? (invoice as { canceledParts?: InvoiceDetailResponse['ignoredParts'] }).canceledParts
            : undefined) ??
        [];

    const restoreIgnoredPart = (index: number) => {
        const currentIgnored = editedFields.ignoredParts ?? invoice?.ignoredParts ?? [];
        const restored = currentIgnored[index];
        if (!restored) return;
        const updatedIgnored = currentIgnored.filter((_, i) => i !== index);
        const findPos = (cur: typeof currentIgnored, orig: typeof currentIgnored | undefined) => {
            if (!orig) return cur.length;
            const ri = orig.findIndex((o) => o.partNumber === restored.partNumber && o.title === restored.title);
            if (ri < 0) return cur.length;
            let insertAt = 0;
            for (let i = 0; i < cur.length; i++) {
                const oi = orig.findIndex((o) => o.partNumber === cur[i].partNumber && o.title === cur[i].title);
                if (oi >= 0 && oi < ri) insertAt = i + 1;
            }
            return insertAt;
        };
        if (isFreightLineItem(restored.partNumber, restored.title)) {
            const freight = editedFields.freightItems ?? invoice?.freightItems ?? [];
            const updated = [...freight];
            updated.splice(findPos(freight, invoice?.originalExtracted?.freightItems), 0, restored);
            setEditedFields((prev) => ({ ...prev, ignoredParts: updatedIgnored, freightItems: updated }));
        } else if (isMiscFeePart(restored.partNumber, restored.title, restored.totalBuyPrice)) {
            const misc = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
            const updated = [...misc];
            updated.splice(findPos(misc, invoice?.originalExtracted?.restockingFeeItems), 0, restored);
            setEditedFields((prev) => ({ ...prev, ignoredParts: updatedIgnored, restockingFeeItems: updated }));
        } else {
            const line = editedFields.lineItems ?? invoice?.lineItems ?? [];
            const updated = [...line];
            const insertAt = findPos(line, invoice?.originalExtracted?.lineItems);
            updated.splice(insertAt, 0, restored);
            reviseLineBackendMap(updated, (map) => lineBackendMapAfterInsert(map, insertAt));
            setEditedFields((prev) => ({ ...prev, ignoredParts: updatedIgnored, lineItems: updated }));
        }
    };

    const updateIgnoredPart = (index: number, field: LineItemField, value: unknown) => {
        const list = getIgnoredList();
        const updated = [...list];
        if (!updated[index]) return;
        const normalizedValue = normalizeReturnLineFieldValue(field, value);
        updated[index] = applyAutoDerivedUnitPrice(updated[index] as InvoicePart, field, normalizedValue);
        setEditedFields((prev) => ({ ...prev, ignoredParts: updated }));
    };

    const { getValue: getIgnoredPartValue, getStringValue: getIgnoredPartStringValue, getPriceValue: getIgnoredPartPriceValue } =
        createPartFieldGetters((index, field) => getIgnoredList()[index]?.[field] ?? '');

    const ignoredAtCallTime = () => editedFields.ignoredParts ?? invoice?.ignoredParts ?? [];

    return {
        updateLineItem,
        restoreLineItemRowsSnapshot,
        addLineItem,
        addLineItemAbove,
        addLineItemAt,
        duplicateLineItemAt,
        removeLineItem,
        getLineItemValue,
        getLineItemStringValue,
        getLineItemPriceValue,
        updateFreightItem: (index: number, field: LineItemField, value: unknown) =>
            updatePartTab('freightItems', index, field, value),
        addFreightItem: () => appendRow('freightItems', newFreightItem()),
        addFreightItemAt: (index: number) => insertRowBelow('freightItems', index, newFreightItem()),
        addFreightItemAbove: (index: number) => insertRowAbove('freightItems', index, newFreightItem()),
        duplicateFreightItemAt: (index: number) => duplicateRowBelow('freightItems', index),
        removeFreightItem: (index: number) => {
            const current = editedFields.freightItems ?? invoice?.freightItems ?? [];
            removeRowWithOptionalMoveToIgnored(setEditedFields, 'freightItems', index, current, ignoredAtCallTime());
        },
        getFreightItemValue,
        getFreightItemStringValue,
        getFreightItemPriceValue,
        updateFuelItem: (index: number, field: LineItemField, value: unknown) =>
            updatePartTab('fuelItems', index, field, value),
        addFuelItem: () => appendRow('fuelItems', newFuelItem()),
        addFuelItemAt: (index: number) => insertRowBelow('fuelItems', index, newFuelItem()),
        removeFuelItem: (index: number) => {
            const current = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
            removeRowWithOptionalMoveToIgnored(setEditedFields, 'fuelItems', index, current, ignoredAtCallTime());
        },
        getFuelItemValue,
        getFuelItemStringValue,
        getFuelItemPriceValue,
        updateRestockingFeeItem: (index: number, field: LineItemField, value: unknown) =>
            updatePartTab('restockingFeeItems', index, field, value),
        addRestockingFeeItem: () => appendRow('restockingFeeItems', newRestockingItem()),
        addRestockingFeeItemAt: (index: number) => insertRowBelow('restockingFeeItems', index, newRestockingItem()),
        addRestockingFeeItemAbove: (index: number) => insertRowAbove('restockingFeeItems', index, newRestockingItem()),
        duplicateRestockingFeeItemAt: (index: number) => duplicateRowBelow('restockingFeeItems', index),
        removeRestockingFeeItem: (index: number) => {
            const current = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
            removeRowWithOptionalMoveToIgnored(setEditedFields, 'restockingFeeItems', index, current, ignoredAtCallTime());
        },
        getRestockingFeeItemValue,
        getRestockingFeeItemStringValue,
        getRestockingFeeItemPriceValue,
        getIgnoredList,
        addIgnoredPart: () =>
            setEditedFields((prev) => ({ ...prev, ignoredParts: [...getIgnoredList(), newLineItem()] })),
        addIgnoredPartAt: (index: number) => {
            const list = getIgnoredList();
            const updated = [...list];
            updated.splice(index + 1, 0, newLineItem());
            setEditedFields((prev) => ({ ...prev, ignoredParts: updated }));
        },
        addIgnoredPartAbove: (index: number) => {
            const list = getIgnoredList();
            const updated = [...list];
            updated.splice(index, 0, newLineItem());
            setEditedFields((prev) => ({ ...prev, ignoredParts: updated }));
        },
        removeIgnoredPart: (index: number) =>
            setEditedFields((prev) => ({ ...prev, ignoredParts: getIgnoredList().filter((_, i) => i !== index) })),
        updateIgnoredPart,
        restoreIgnoredPart,
        getIgnoredPartValue,
        getIgnoredPartStringValue,
        getIgnoredPartPriceValue,
    };
}
